import type React from 'react';
import {useState} from 'react';
import {Box, Button, Stack, Table, TableBody, TableCell, TableHead, TableRow, Typography} from '@mui/material';
import {Add} from '@mui/icons-material';
import {Tag} from '~/components/tag/Tag';
import {useExploreQuery} from '../hooks/useCategoryProfileQueries';
import {useStaffSignalsActions, useStaffSignalsQuery} from '../hooks/useSignals';
import {RowAction} from '../profile/components/RowAction';
import {SignalDialog} from './SignalDialog';
import {LOCALE} from '~/pages/category-profile/domain/locale';

const WHEN = new Intl.DateTimeFormat(LOCALE, {day: 'numeric', month: 'short', year: 'numeric'});
const IMPACT_COLOR = {high: 'error', medium: 'warning', low: 'default'} as const;

/** The dataset's external signals, for staff: add by hand while the provider feed is off, or remove (with Undo). */
export const SignalsSettings: React.FC<{databagId: number}> = ({databagId}) => {
    const {data} = useStaffSignalsQuery(databagId, true);
    const explore = useExploreQuery(databagId);
    const actions = useStaffSignalsActions(databagId);
    const [adding, setAdding] = useState(false);
    if (!data) return null;

    return (
        <Stack gap={1.5} sx={{mt: 2}}>
            <Stack direction="row" alignItems="center" gap={2}>
                <Typography variant="subtitle1" sx={{flex: 1}}>{`Signals in this dataset (${data.signals.length})`}</Typography>
                <Tag label={data.providerEnabled ? 'Provider feed on' : 'Provider feed off'} color={data.providerEnabled ? 'success' : 'default'}/>
                <Button size="small" variant="outlined" startIcon={<Add/>} disabled={!explore.data}
                        onClick={() => setAdding(true)}>Add signal</Button>
            </Stack>
            {!data.providerEnabled && (
                <Typography variant="caption" color="text.secondary">
                    The svEye feed needs its licence and SVEYE_API_KEY in the deployment settings, plus "External provider feed"
                    above. Until then, signals added here are the only source.
                </Typography>
            )}
            {data.signals.length > 0 && (
                <Box sx={{overflowX: 'auto'}}>
                    <Table size="small" aria-label="External signals in this dataset">
                        <TableHead>
                            <TableRow>
                                <TableCell>Published</TableCell>
                                <TableCell>Signal</TableCell>
                                <TableCell>Category</TableCell>
                                <TableCell>Impact</TableCell>
                                <TableCell align="right">Relevance</TableCell>
                                <TableCell/>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {data.signals.map(s => (
                                <TableRow key={s.id}>
                                    <TableCell sx={{whiteSpace: 'nowrap'}}>{WHEN.format(new Date(s.publishedAt))}</TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{s.title}</Typography>
                                        <Typography variant="caption" color="text.secondary">{`${s.publisher || '—'} · ${s.source}`}</Typography>
                                    </TableCell>
                                    <TableCell>{s.path.split('|').join(' › ')}</TableCell>
                                    <TableCell><Tag label={s.impact} color={IMPACT_COLOR[s.impact]} solid={false}/></TableCell>
                                    <TableCell align="right">{s.relevance}</TableCell>
                                    <TableCell align="right">
                                        <RowAction kind="delete" label="signal" onClick={() => void actions.remove(s.id, s.title)}/>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </Box>
            )}
            {adding && explore.data && (
                <SignalDialog tree={explore.data.tree} options={data.options} onSubmit={actions.add} onClose={() => setAdding(false)}/>
            )}
        </Stack>
    );
};
